import { motion } from "framer-motion";
import { Award, Sparkles } from "lucide-react";

/**
 * MasterySeal — animated certificate / achievement medallion.
 *
 * Renders a glowing circular seal with:
 *  - Concentric rotating ring (CSS keyframes)
 *  - Gold-foil gradient core with the course goal embossed
 *  - Sparkle particles orbiting in
 *  - Footer with score + minted-at timestamp
 *
 * Used in two places:
 *  1. End-of-Boss-Quiz celebration (full animation on mount).
 *  2. Library cert wall (smaller variant, animation skipped).
 *
 * `variant`:
 *  - "full"  — large hero, full animation. (default)
 *  - "small" — compact card for cert walls; no entrance animation.
 */
export default function MasterySeal({
  pathGoal,
  score = 0,
  total = 10,
  mintedAt,
  variant = "full",
}) {
  const goalShort = (pathGoal || "Course").slice(0, 64);
  const pct = total > 0 ? Math.round((score / total) * 100) : 100;
  const mintedDate = mintedAt
    ? new Date(mintedAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : null;

  if (variant === "small") {
    return (
      <div
        className="relative w-full rounded-2xl border border-[#FFD700]/22 bg-gradient-to-br from-[#1a1407] to-[#0B0E14] p-4 overflow-hidden"
        data-testid="mastery-seal-small"
      >
        <div className="flex items-center gap-3">
          <div className="relative h-12 w-12 shrink-0">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FFD700]/35 to-[#FFB067]/25 blur-md" />
            <div className="relative h-12 w-12 rounded-full grid place-items-center bg-gradient-to-br from-[#FFD700]/25 to-[#FFB067]/15 border border-[#FFD700]/45">
              <Award size={18} className="text-[#FFD700]" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-[13px] text-white truncate">{goalShort}</div>
            <div className="text-[10px] text-white/40 font-mono-oak mt-0.5">
              {score}/{total} · {pct}%{mintedDate ? ` · ${mintedDate}` : ""}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ scale: 0.4, opacity: 0, rotate: -12 }}
      animate={{ scale: 1, opacity: 1, rotate: 0 }}
      transition={{ type: "spring", stiffness: 120, damping: 12, mass: 0.9 }}
      className="relative inline-block"
      data-testid="mastery-seal-full"
    >
      {/* Outer glow */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FFD700]/45 to-[#FF6BAA]/30 blur-3xl scale-110 pointer-events-none" />

      {/* Rotating ring */}
      <motion.div
        className="absolute -inset-1.5 rounded-full"
        style={{
          background: "conic-gradient(from 0deg, #FFD700, #FF6BAA, #5D3FD3, #00FF9D, #FFD700)",
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 6, ease: "linear", repeat: Infinity }}
      />

      {/* Inner medallion */}
      <div className="relative h-44 w-44 sm:h-52 sm:w-52 rounded-full grid place-items-center bg-gradient-to-br from-[#1a1407] via-[#0B0E14] to-[#100a18] border-2 border-[#FFD700]/40 shadow-[0_25px_60px_-15px_rgba(255,215,0,0.45)]">
        {/* Soft inner ring */}
        <div className="absolute inset-3 rounded-full border border-[#FFD700]/22" />
        <div className="absolute inset-5 rounded-full border border-white/[0.05]" />

        {/* Sparkles */}
        {[
          { top: "8%", left: "50%", delay: 0.1 },
          { top: "50%", left: "92%", delay: 0.3 },
          { top: "88%", left: "50%", delay: 0.5 },
          { top: "50%", left: "8%",  delay: 0.7 },
        ].map((s, i) => (
          <motion.span
            key={i}
            className="absolute -translate-x-1/2 -translate-y-1/2 text-[#FFD700]"
            style={{ top: s.top, left: s.left }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 0.4], scale: [0, 1, 0.85] }}
            transition={{ duration: 1.5, delay: s.delay, repeat: Infinity, repeatDelay: 2 }}
            aria-hidden
          >
            <Sparkles size={11} />
          </motion.span>
        ))}

        {/* Core content */}
        <div className="relative text-center px-4 z-10">
          <motion.div
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-[8.5px] uppercase tracking-[0.28em] text-[#FFD700] font-display font-bold"
          >
            Mastery
          </motion.div>
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.45, type: "spring", stiffness: 220 }}
            className="my-1.5"
          >
            <Award size={36} className="text-[#FFD700] mx-auto drop-shadow-[0_0_12px_rgba(255,215,0,0.6)]" />
          </motion.div>
          <motion.div
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-[10.5px] font-display text-white leading-tight line-clamp-2 max-w-[120px] mx-auto"
          >
            {goalShort}
          </motion.div>
          <motion.div
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.75 }}
            className="text-[8.5px] text-white/45 font-mono-oak mt-1"
          >
            {score}/{total} · {pct}%
          </motion.div>
          {mintedDate && (
            <motion.div
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="text-[8px] text-white/35 font-mono-oak tracking-wide mt-0.5"
            >
              {mintedDate}
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
